// GOLDEN PATH CODE
// This is a table backed by data entirely retrieved from react-query.
// Based on https://cloudscape.aws.dev/examples/react/server-side-table.html
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UseCollectionResult } from '@amzn/awsui-collection-hooks';
import {
  Button,
  CopyToClipboard,
  Header,
  Pagination,
  SpaceBetween,
  Table,
  TableProps,
} from '@amzn/awsui-components-react';

import { AttendanceStatus, Shift, ShiftType } from '@amzn/red-velvet-api';
import { ShiftSelect } from '../coordinator/shiftSelect';
import { formatShiftDate, formatShiftTime } from '../../../utils/dateFormatting';

import { useLocalizedText } from '../../../hooks/localizedText';
import { useLocale } from '../../../hooks/locale';
import { useTimezone } from '../../../hooks/timezone';
import { useTableStrings } from '../../../hooks/localization/tableStrings';
import { usePaginationStrings } from '../../../hooks/localization/paginationStrings';
import { useAttendeeSessionStorage } from '../../../hooks/attendance/attendeeSessionStorage';
import { useTableColumns } from '../../../hooks/tableColumns';
import { getValueFromSearchParam } from '../../../utils/url';
import { useParticipants } from '../../../hooks/attendance/useParticipants';
import { TablePropertyFilter } from '../wizard/tablePropertyFilter';
import { usePropertyFilterStrings } from '../../../hooks/localization/propertyFilterStrings';
import { useParticipantSessionStorage } from '../../../hooks/attendance/participantSessionStorage';
import {
  useBulkParticipantSessionStorage,
  BulkParticipantData,
} from '../../../hooks/attendance/bulkParticipantSessionStorage';
import {
  Amazonian,
  columnMap,
  shiftAttendanceColumnMap,
  eventLevelAttendanceColumnMap,
  getAliases,
} from '../../../utils/attendance/peopleSearch';
import { publishKatalMetric } from '../../katalAnalytics';
import { PeoplePreferences } from '../wizard/peoplePreferences';

import { AttendanceSelect } from '../coordinator/attendanceSelect';
import { useUser } from '../../../hooks/user';

export type AttendeeTableProps = {
  isImmutable?: boolean;
  onAddAttendees?: () => void;
  shifts?: Shift[];
  vcCreated?: boolean;
} & Partial<TableProps>;

export type ParticipationStatus = AttendanceStatus | 'PENDING';

export function CsvActions(props: {
  selectedItems: readonly Amazonian[];
  collection: UseCollectionResult<Amazonian>;
}) {
  const namespaces = ['attendance', 'translation'];
  const { t } = useLocalizedText(namespaces);

  const { aliases, removeAttendees } = useAttendeeSessionStorage();

  return (
    <SpaceBetween direction="horizontal" size="xs">
      <CopyToClipboard
        copyButtonText={t('attendeeTable.copyAttendees')}
        copyErrorText={t('attendeeTable.copyAttendeesFailed')}
        copySuccessText={t('attendeeTable.copyAttendeesSuccess')}
        data-aci-analytics-name="attendance_copyAliasList"
        textToCopy={
          props.selectedItems.length > 0
            ? getAliases(props.selectedItems).join(',')
            : aliases.join(',')
        }
      />
      <Button
        variant="primary"
        disabled={props.selectedItems.length === 0}
        onClick={() => {
          if (props.collection.collectionProps.onSelectionChange) {
            const emptyItems: Amazonian[] = [];
            props.collection.collectionProps.onSelectionChange({
              detail: { selectedItems: emptyItems },
            });
          }
          props.collection.paginationProps.onChange({ detail: { currentPageIndex: 1 } });
          const aliasesToRemove = getAliases(props.selectedItems);
          const urlAlises = aliasesToRemove.filter((alias) => aliases.includes(alias));
          removeAttendees(urlAlises);
          publishKatalMetric('attendance_removeAttendees', aliases.length.toString());
        }}
      >
        {t('attendeeTable.removeAttendees')}
      </Button>
    </SpaceBetween>
  );
}

export function TrackedActions(props: {
  selectedItems: readonly Amazonian[];
  collection: UseCollectionResult<Amazonian>;
  isEventLevelTracking: boolean;
}) {
  const namespaces = ['attendance', 'translation'];
  const { t } = useLocalizedText(namespaces);

  const { updateBulkParticipants } = useBulkParticipantSessionStorage();

  return (
    <SpaceBetween direction="horizontal" size="xs">
      <Button
        disabled={props.selectedItems.length === 0}
        onClick={() => {
          const mixin: BulkParticipantData = {};
          props.selectedItems.forEach((item) => {
            // Event-level: use composite key 'alias-shiftId' to target specific shift
            // Single-shift: use 'alias' to target the single participation
            const key =
              props.isEventLevelTracking && item.shiftId
                ? `${item.alias}-${item.shiftId}`
                : item.alias;
            mixin[key] = {
              ...item,
              originalShiftId: item.originalShiftId || item.shiftId,
              attendance: 'DID_NOT_ATTEND',
            };
          });
          updateBulkParticipants(mixin);
          publishKatalMetric(
            'attendance_removeAttendeesPage1',
            props.selectedItems.length.toString()
          );
        }}
      >
        {t('attendeeTable.coordinatorTracking.setDidNotAttend')}
      </Button>
      <Button
        variant="primary"
        disabled={props.selectedItems.length === 0}
        onClick={() => {
          const mixin: BulkParticipantData = {};
          props.selectedItems.forEach((item) => {
            // Event-level: use composite key 'alias-shiftId' to target specific shift
            // Single-shift: use 'alias' to target the single participation
            const key =
              props.isEventLevelTracking && item.shiftId
                ? `${item.alias}-${item.shiftId}`
                : item.alias;
            mixin[key] = {
              ...item,
              originalShiftId: item.originalShiftId || item.shiftId,
              attendance: 'ATTENDED',
            };
          });
          updateBulkParticipants(mixin);
          publishKatalMetric('attendance_addAttendeesPage1', props.selectedItems.length.toString());
        }}
      >
        {t('attendeeTable.coordinatorTracking.setAttended')}
      </Button>
    </SpaceBetween>
  );
}

export function AttendeeTable(props: AttendeeTableProps) {
  const namespaces = ['attendance', 'translation'];
  const { t } = useLocalizedText(namespaces);
  const locale = useLocale();
  const timezone = useTimezone();
  const { userId } = useUser();
  const { updateParticipants } = useParticipantSessionStorage();
  const { updateBulkParticipants, updateBulkParticipantsFromParticipantData } =
    useBulkParticipantSessionStorage();

  const [searchParams] = useSearchParams();
  const eventId = getValueFromSearchParam(searchParams, 'eventId', '');
  const shiftId = getValueFromSearchParam(searchParams, 'shiftId', '');

  const isEventLevelTracking = !!eventId && !shiftId;

  // Filter out WAITLIST shifts for dropdown options
  const selectableShifts = React.useMemo(
    () => (props.shifts || []).filter((shift) => shift.shiftType !== ShiftType.WAITLIST),
    [props.shifts]
  );

  // Create a map of shiftId -> Shift for quick lookup when handling shift changes
  const shiftMap = React.useMemo(() => {
    const map = new Map<string, Shift>();
    selectableShifts.forEach((shift) => {
      if (shift.shiftId) {
        map.set(shift.shiftId, shift);
      }
    });
    return map;
  }, [selectableShifts]);

  const tableStrings = useTableStrings('attendeeTable', (item: Amazonian) => item.name, namespaces);
  const paginationStrings = usePaginationStrings('attendeeTable', namespaces);

  // GOLDEN PATH NOTE
  // Table controls such as selectedItems, pagination, and filters should be controlled by state if it is unlikely
  // that the user cares to replicate them across sessions
  const [pageSize, setPageSize] = useState(100);

  const propertyFilterStrings = usePropertyFilterStrings('propertyFilter', namespaces);
  const collection = useParticipants(
    namespaces,
    'coordinatorAttendeeTable',
    eventId,
    shiftId,
    pageSize,
    props.shifts
  );
  const {
    items,
    participants,
    rawParticipants,
    isLoading,
    collectionProps,
    paginationProps,
    propertyFilterProps,
  } = collection;

  const { selectedItems = [] } = collectionProps;

  // Get shifts already assigned to a user (for disabling in dropdown)
  const getDisabledShiftIds = (item: Amazonian) => {
    return participants
      .filter((p) => p.alias === item.alias && p.shiftId !== item.shiftId)
      .map((p) => p.shiftId)
      .filter((id): id is string => !!id);
  };

  const getColumnMap = () => {
    if (isEventLevelTracking) {
      return eventLevelAttendanceColumnMap;
    }
    if (eventId && shiftId) {
      return shiftAttendanceColumnMap;
    }
    return columnMap;
  };

  const colMap = getColumnMap();
  const columns = useTableColumns(colMap, 'attendeeTable', namespaces, {
    attendance: {
      errorIconAriaLabel: 'State Validation Error',
      editingCell: (item, { currentValue, setValue }) => (
        <AttendanceSelect
          participant={item}
          currentValue={currentValue || item.attendance}
          onAttendanceChange={(status: ParticipationStatus) => {
            setValue(status);
          }}
        />
      ),
      disabledReason: (item) =>
        item.alias === item.trackedBy && item.alias !== userId
          ? t('coordinatorAttendeeTable.selfTracked', { ns: 'attendance' })
          : undefined,
    },
    ...(isEventLevelTracking && selectableShifts.length > 0
      ? {
          shiftName: {
            editingCell: (item, { currentValue, setValue }) => (
              <ShiftSelect
                shifts={selectableShifts}
                currentShiftId={currentValue ?? item.shiftId}
                disabledShiftIds={getDisabledShiftIds(item)}
                onShiftChange={(shiftId) => setValue(shiftId)}
              />
            ),
          },
        }
      : {}),
  });

  return (
    <Table
      {...props}
      header={
        <Header
          actions={
            props.isImmutable ? null : (
              <SpaceBetween direction="horizontal" size="xs">
                {props.onAddAttendees && (
                  <Button onClick={props.onAddAttendees}>
                    {t('bulkUpload.addAttendeesAction')}
                  </Button>
                )}
                <TrackedActions
                  collection={collection}
                  selectedItems={selectedItems}
                  isEventLevelTracking={isEventLevelTracking}
                />
              </SpaceBetween>
            )
          }
          counter={
            selectedItems.length
              ? `(${selectedItems.length}/${participants.length})`
              : `(${participants.length})`
          }
        >
          {t('attendeeTable.title')}
        </Header>
      }
      filter={
        <TablePropertyFilter
          {...propertyFilterProps}
          propertyFilterStrings={propertyFilterStrings}
          filteringPlaceholder={t('coordinatorAttendeeTable.filteringPlaceholder')}
        />
      }
      pagination={<Pagination {...paginationProps} ariaLabels={paginationStrings} />}
      preferences={
        <PeoplePreferences
          onConfirm={({ detail }) => {
            paginationProps.onChange({ detail: { currentPageIndex: 1 } });
            setPageSize(detail.pageSize || 100);
          }}
          preferences={{
            pageSize,
          }}
        />
      }
      selectionType={props.isImmutable ? undefined : 'multi'}
      submitEdit={(item, column, newVal) => {
        if (column.id === 'shiftName' && isEventLevelTracking) {
          const newShiftId = newVal as string;
          const shift = shiftMap.get(newShiftId);
          const originalShiftId = item.originalShiftId || item.shiftId;
          const key = `${item.alias}-${originalShiftId}`;

          // Look up existing participation data for this user + new shift from original API data
          const existingParticipation = rawParticipants.find(
            (p) => p.volunteerAlias === item.alias && p.shiftId === newShiftId
          );

          updateBulkParticipants({
            [key]: {
              alias: item.alias,
              name: item.name,
              building: item.building,
              managerLogin: item.managerLogin,
              originalShiftId,
              shiftId: newShiftId,
              shiftName: shift?.name || '',
              shiftDate: formatShiftDate(shift?.startDateTime, timezone, locale),
              shiftTime: formatShiftTime(shift?.startDateTime, timezone, locale),
              signedUp: existingParticipation?.isSignedUp ?? false,
              trackedBy: existingParticipation?.attendanceUpdatedByAlias || userId,
              attendance: existingParticipation?.attendanceStatus || AttendanceStatus.ATTENDED,
              trackedTime: existingParticipation?.attendanceMinutes ?? 0,
            },
          });
        } else if (isEventLevelTracking) {
          const key = `${item.alias}-${item.shiftId}`;
          updateBulkParticipants({
            [key]: {
              ...item,
              originalShiftId: item.originalShiftId || item.shiftId,
              attendance: newVal as string,
            },
          });
        } else {
          const mixin = {
            [item.alias || '']: {
              attendance: newVal as string,
            },
          };
          updateParticipants(mixin);
          updateBulkParticipantsFromParticipantData(mixin);
        }
      }}
      // Do not allow modifications if the user tracked their own attendance
      isItemDisabled={(item) => item.alias === item.trackedBy && item.alias !== userId}
      {...tableStrings}
      {...columns}
      {...collectionProps}
      loading={isLoading}
      items={items}
      trackBy={isEventLevelTracking ? (item) => `${item.alias}-${item.shiftId}` : 'alias'}
      data-testid={'attendee-table'}
    />
  );
}
